import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, ChevronLeft, ChevronRight, Loader2, Save } from "lucide-react";
import { api, apiErr } from "../api";
import { BRAND } from "../brand";
import { Modal, MONTHS, toISO, prettyDate, STATUS_LABEL } from "../ui";
import { handleSendResult } from "./SendResult";

const pad = (n) => String(n).padStart(2, "0");
const SLOTS = (() => {
  const s = [];
  const [h0, h1] = BRAND.slotHours || [8, 18];
  for (let h = h0; h < h1; h++) { s.push(`${pad(h)}:00`); s.push(`${pad(h)}:30`); }
  return s;
})();
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Crear o editar una cita desde la Agenda (calendario + horarios libres). */
export default function AppointmentModal({ initial, patients, taken, onClose, onSaved, presetPatient }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState(() => ({
    patient_id: initial?.patient_id || presetPatient?.id || "",
    nombre: initial?.nombre || presetPatient?.nombre || "", telefono: initial?.telefono || presetPatient?.telefono || "",
    email: initial?.email || presetPatient?.email || "", cedula: initial?.cedula || presetPatient?.cedula || "",
    servicio: initial?.servicio || "", nota: initial?.nota || "", fecha: initial?.fecha || "", hora: initial?.hora || "",
    status: initial?.status || "pendiente",
  }));
  const [notify, setNotify] = useState(editing ? "none" : "both");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(() => {
    const d = initial?.fecha ? new Date(initial.fecha + "T12:00") : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const pickPatient = (e) => {
    const p = patients.find((x) => x.id === e.target.value);
    setForm((f) => ({ ...f, patient_id: e.target.value, ...(p ? { nombre: p.nombre, telefono: p.telefono || "", email: p.email || "", cedula: p.cedula || "" } : {}) }));
  };

  const takenTimes = useMemo(() => taken.filter((a) => a.fecha === form.fecha && ["pendiente", "confirmada", "reprogramar"].includes(a.status) && a.id !== initial?.id).map((a) => a.hora),
    [taken, form.fecha, initial]);
  const busyDays = useMemo(() => {
    const m = {};
    taken.forEach((a) => { if (a.status !== "cancelada") m[a.fecha] = (m[a.fecha] || 0) + 1; });
    return m;
  }, [taken]);

  const first = new Date(view.y, view.m, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => new Date(view.y, view.m, i + 1))];
  const move = (n) => setView((v) => { const d = new Date(v.y, v.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Elige un paciente o escribe el nombre"); return; }
    if (!form.fecha) { toast.error("Elige la fecha en el calendario"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/appointments/${initial.id}`, form);
        if (notify !== "none") {
          const { data } = await api.post(`/appointments/${initial.id}/notify`, { channel: notify });
          handleSendResult(data, notify);
        } else toast.success("Cita actualizada");
      } else {
        const { data } = await api.post("/appointments", { ...form, notify });
        handleSendResult(data, notify === "none" ? "" : notify);
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e, "No se pudo guardar la cita")); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={editing ? "Editar cita" : "Nueva cita"} subtitle={form.fecha ? `${prettyDate(form.fecha, true)}${form.hora ? " · " + form.hora : ""}` : "Elige fecha y hora"}
      icon={CalendarPlus} onClose={onClose} maxWidth="max-w-4xl"
      footer={<>
        <button onClick={onClose} className="pn-btn pn-btn-ghost">Cancelar</button>
        <button onClick={save} disabled={busy} className="pn-btn pn-btn-primary">{busy ? <Loader2 size={16} className="pn-spin" /> : <Save size={16} />} {editing ? "Guardar cambios" : "Agendar cita"}</button>
      </>}>
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => move(-1)} className="pn-icon-btn" aria-label="Mes anterior"><ChevronLeft size={17} /></button>
            <span className="font-bold pn-ink">{MONTHS[view.m]} {view.y}</span>
            <button onClick={() => move(1)} className="pn-icon-btn" aria-label="Mes siguiente"><ChevronRight size={17} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold pn-muted mb-1">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <span key={i} />;
              const iso = toISO(d);
              const past = d < today;
              const sunday = d.getDay() === 0 && !BRAND.openSundays;
              return (
                <button key={i} type="button" disabled={past || sunday} aria-pressed={form.fecha === iso}
                  onClick={() => setForm((f) => ({ ...f, fecha: iso }))} className="pn-cal-day pn-ink relative">
                  {d.getDate()}
                  {busyDays[iso] && form.fecha !== iso && <span className="absolute bottom-1 w-1 h-1 rounded-full" style={{ background: "var(--brand)" }} />}
                </button>
              );
            })}
          </div>
          {form.fecha && (
            <div className="mt-4">
              <p className="pn-label">Hora {takenTimes.length > 0 && <span className="pn-muted font-normal">· tachadas = ocupadas</span>}</p>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
                {SLOTS.map((s) => (
                  <button key={s} type="button" className="pn-slot" disabled={takenTimes.includes(s)} aria-pressed={form.hora === s}
                    onClick={() => setForm((f) => ({ ...f, hora: f.hora === s ? "" : s }))}>{s}</button>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs pn-sub">Otra hora: <input type="time" value={form.hora} onChange={up("hora")} className="pn-field !w-auto !py-1" /></div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="pn-label">Paciente registrado</label>
            <select value={form.patient_id} onChange={pickPatient} className="pn-field">
              <option value="">— Paciente nuevo / no registrado —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.cedula ? ` · ${p.cedula}` : ""}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="pn-label">Nombre *</label><input value={form.nombre} onChange={up("nombre")} className="pn-field" /></div>
            <div><label className="pn-label">Teléfono / WhatsApp</label><input value={form.telefono} onChange={up("telefono")} placeholder="09XXXXXXXX" className="pn-field" /></div>
            <div><label className="pn-label">Email</label><input type="email" value={form.email} onChange={up("email")} className="pn-field" /></div>
            <div><label className="pn-label">Servicio</label>
              <select value={form.servicio} onChange={up("servicio")} className="pn-field">
                <option value="">Consulta</option>{BRAND.services.map((s) => <option key={s}>{s}</option>)}
              </select></div>
            {editing ? (
              <div><label className="pn-label">Estado</label>
                <select value={form.status} onChange={up("status")} className="pn-field">
                  {["pendiente", "confirmada", "reprogramar", "cancelada", "atendida"].map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select></div>
            ) : (
              <div><label className="pn-label">Cédula</label><input value={form.cedula} onChange={up("cedula")} className="pn-field" /></div>
            )}
            <div className="sm:col-span-2"><label className="pn-label">Motivo / nota</label><input value={form.nota} onChange={up("nota")} className="pn-field" /></div>
          </div>
          <div>
            <label className="pn-label">{editing ? "¿Reenviar al paciente?" : "Enviar al paciente"}</label>
            <div className="grid grid-cols-4 gap-1.5">
              {[["none", "No enviar"], ["email", "Email"], ["whatsapp", "WhatsApp"], ["both", "Ambos"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setNotify(id)} className={`pn-btn !px-2 !py-2 text-xs ${notify === id ? "pn-btn-soft" : "pn-btn-ghost"}`}>{label}</button>
              ))}
            </div>
            <p className="text-[11px] pn-muted mt-1.5">Incluye el enlace para que confirme o pida otra fecha.</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
